import React from "react"
import { doc, getDoc } from "firebase/firestore"
import { ComponentProps, ReactNode, useEffect, useState, useMemo } from "react"
import { auth } from "../firebaseAuth"
import { db } from "../firebaseFirestore"

import { useAuthState } from "react-firebase-hooks/auth"
import { LocationContext } from "react-ui-components/components/location/locationContext"
import { ILocationItem } from "react-ui-components/components/models/ILocation"
import {
  applyUrlContext,
  normalizeLocationContext,
  readUrlContext,
  resolveInitialLocation,
} from "../utils/urlContext"

export const LocationService = {
  setLocation: (location: any) => {},
  getLocation: (): any => {},
  setAvailableLocations: (val: any) => {},
}

export interface ICountry {
  currency: string
  disabled: boolean
  display: string
  icon: string
  key: string
}
export interface ILocation {
  [key: string]: {
    [key: string]: string[]
  }
}

export const DEFAULT_AVAILABLE_LOCATION = {}

const EMPTY_LOCATION: ILocationItem = {
  country: "",
  city: "",
  state: "",
  zip: "",
}

export const getSavedLocation = (): ILocationItem => {
  const raw = localStorage.getItem("WINLOC")
  if (!raw) return EMPTY_LOCATION

  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") {
      return EMPTY_LOCATION
    }
    return {
      ...EMPTY_LOCATION,
      ...parsed,
      ...normalizeLocationContext(parsed),
    }
  } catch {
    // If the stored value is invalid JSON, start fresh instead of crashing the app shell.
    return EMPTY_LOCATION
  }
}

export const getInitialLocation = (): ILocationItem => {
  const savedLocation = getSavedLocation()
  const resolved = resolveInitialLocation(
    typeof window !== "undefined" ? window.location.search : "",
    savedLocation,
  )

  return {
    ...EMPTY_LOCATION,
    ...resolved,
  }
}

const getAvailableLocations = async (
  setData: (data: ILocation) => void,
  setLoading: (loading: boolean) => void,
) => {
  setLoading(true)
  try {
    const querySnapshot = doc(db, "app-configurations/availableLocations")

    const ref = await getDoc(querySnapshot)
    const data = (ref.data() || {}) as ILocation
    setData && setData(data)
    return data
  } catch {
    return DEFAULT_AVAILABLE_LOCATION
  } finally {
    setLoading(false)
  }
}

export const LocationProvider: React.FC<{
  children: ReactNode
  defaultLocation?: ILocation
}> = (props) => {
  const { children, defaultLocation = DEFAULT_AVAILABLE_LOCATION } = props
  const [availableLocations, setAvailableLocations] = useState<ILocation>(
    defaultLocation || DEFAULT_AVAILABLE_LOCATION,
  )
  const [loading, setLoading] = useState(false)
  const [user] = useAuthState(auth)
  const [currentLocation, setCurrentLocationState] = useState<ILocationItem>(getInitialLocation)

  const setCurrentLocation = React.useCallback(
    (next: React.SetStateAction<ILocationItem>) => {
      setCurrentLocationState((prev) => {
        const resolved = typeof next === "function" ? next(prev) : next
        if (!resolved || typeof resolved !== "object") {
          return EMPTY_LOCATION
        }
        return {
          ...EMPTY_LOCATION,
          ...resolved,
          ...normalizeLocationContext(resolved),
        }
      })
    },
    [],
  )

  LocationService.setLocation = setCurrentLocation
  LocationService.getLocation = () => currentLocation
  LocationService.setAvailableLocations = setAvailableLocations

  useEffect(() => {
    LocationService.setLocation = setCurrentLocation
    LocationService.setAvailableLocations = setAvailableLocations
    user && getAvailableLocations(setAvailableLocations, setLoading)
  }, [setCurrentLocation, user])

  useEffect(() => {
    LocationService.getLocation = () => currentLocation
    localStorage.setItem("WINLOC", JSON.stringify(currentLocation))

    if (typeof window !== "undefined") {
      const paramsWithContext = new URLSearchParams(window.location.search)
      const prev = paramsWithContext.toString()
      const urlContext = readUrlContext(paramsWithContext)

      applyUrlContext(paramsWithContext, {
        language: urlContext.language,
        location: currentLocation,
      })

      const next = paramsWithContext.toString()
      if (next !== prev) {
        const nextUrl = `${window.location.pathname}${next ? `?${next}` : ""}${window.location.hash}`
        window.history.replaceState(window.history.state, "", nextUrl)
      }
    }
  }, [currentLocation])

  const contextValue = useMemo(
    () => ({
      availableLocations,
      currentLocation,
      setCurrentLocation,
      setAvailableLocations,
      loading,
    }),
    [availableLocations, currentLocation, loading, setCurrentLocation],
  )

  return (
    <LocationContext.Provider value={contextValue}>
      {children as unknown as ComponentProps<typeof LocationContext.Provider>["children"]}
    </LocationContext.Provider>
  )
}
